import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import {
  Users, AlertTriangle, UserPlus, ArrowRight, Settings as SettingsIcon,
  PauseCircle, Globe, Save, X, Check, ChevronRight, RotateCcw,
} from 'lucide-react';
import { AvailabilityPill, Avatar } from '../../components/SpecialistCard';
import { toast } from 'sonner';
import { fmtDateTime } from '../../lib/docs';

const TABS = [
  { key: 'workloads',  label: 'Workloads & capacity', icon: Users },
  { key: 'requests',   label: 'Change requests',     icon: ArrowRight },
  { key: 'unassigned', label: 'Unassigned members',  icon: UserPlus },
];

function Bar({ value, max }) {
  if (!max || max <= 0) {
    return (
      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full bg-neutral-300" style={{ width: '6%' }} />
      </div>
    );
  }
  const pct = Math.min(100, Math.round((value / max) * 100));
  const tone = pct >= 100 ? '#e11d48' : pct >= 80 ? '#f59e0b' : '#f5b300';
  return (
    <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${pct}%`, background: tone }} />
    </div>
  );
}

function CapacityEditor({ specialist, onSaved }) {
  const [cap, setCap] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminGetSpecialistCapacity(specialist.id).then(setCap).catch(() => setCap({}));
  }, [specialist.id]);

  const update = (patch) => { setCap((c) => ({ ...c, ...patch })); setDirty(true); };
  const upd = (k) => (e) => {
    const v = e.target.value;
    update({ [k]: v === '' ? null : Math.max(0, parseInt(v, 10) || 0) });
  };
  const addVacation = () => update({
    vacation_periods: [...(cap?.vacation_periods || []), { start_date: '', end_date: '', reason: '' }],
  });
  const updateVac = (i, patch) => update({
    vacation_periods: (cap.vacation_periods || []).map((v, idx) => idx === i ? { ...v, ...patch } : v),
  });
  const delVac = (i) => update({
    vacation_periods: (cap.vacation_periods || []).filter((_, idx) => idx !== i),
  });

  const save = async () => {
    setSaving(true);
    try {
      const valid = (cap.vacation_periods || []).filter((v) => v.start_date && v.end_date);
      const updated = await api.adminSetSpecialistCapacity(specialist.id, { ...cap, vacation_periods: valid });
      setCap(updated); setDirty(false);
      toast.success('Capacity saved');
      onSaved && onSaved();
    } catch {
      toast.error('Could not save capacity');
    } finally { setSaving(false); }
  };

  if (!cap) return <div className="h-[100px] bg-neutral-100 animate-pulse rounded-md" />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { k: 'max_daily_appointments',  label: 'Max appts / day' },
          { k: 'max_weekly_appointments', label: 'Max appts / week' },
          { k: 'max_active_clients',      label: 'Max active clients' },
        ].map((f) => (
          <label key={f.k} className="block">
            <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{f.label}</span>
            <input data-testid={`cap-input-${f.k}-${specialist.id}`}
                   type="number" min="0"
                   value={cap[f.k] ?? ''} onChange={upd(f.k)}
                   placeholder="∞"
                   className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none" />
          </label>
        ))}
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input data-testid={`cap-allow-bookings-${specialist.id}`}
               type="checkbox"
               checked={!!cap.allow_new_bookings}
               onChange={(e) => update({ allow_new_bookings: e.target.checked })}
               className="w-4 h-4 accent-[#f5b300]" />
        <span className="text-[13.5px] text-[#14110f]">Accept new bookings</span>
      </label>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Vacation periods</p>
          <button data-testid={`cap-add-vac-${specialist.id}`}
                  onClick={addVacation}
                  className="text-[12px] font-semibold text-[#c89200] hover:underline">+ Add period</button>
        </div>
        {(cap.vacation_periods || []).length === 0 && (
          <p className="text-[12px] text-neutral-400">No vacation periods set.</p>
        )}
        <div className="space-y-2">
          {(cap.vacation_periods || []).map((v, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-7 gap-2 items-center">
              <input type="date" value={v.start_date} onChange={(e) => updateVac(i, { start_date: e.target.value })}
                     className="md:col-span-2 px-2 py-1.5 rounded-md border border-neutral-200 text-[12.5px]" />
              <input type="date" value={v.end_date} onChange={(e) => updateVac(i, { end_date: e.target.value })}
                     className="md:col-span-2 px-2 py-1.5 rounded-md border border-neutral-200 text-[12.5px]" />
              <input value={v.reason} onChange={(e) => updateVac(i, { reason: e.target.value })}
                     placeholder="Reason (optional)"
                     className="md:col-span-2 px-2 py-1.5 rounded-md border border-neutral-200 text-[12.5px]" />
              <button onClick={() => delVac(i)} className="text-rose-500 text-[11.5px] font-semibold hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-3">
        <button data-testid={`cap-save-${specialist.id}`}
                disabled={!dirty || saving}
                onClick={save}
                className="inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] disabled:bg-neutral-200 disabled:text-neutral-500 text-[#14110f] font-semibold px-4 py-2 rounded-md text-[12.5px]">
          <Save className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save capacity'}
        </button>
      </div>
    </div>
  );
}

function WorkloadCard({ specialist, onOpenReassign, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const cap = specialist.capacity || {};
  return (
    <article data-testid={`workload-card-${specialist.id}`}
             className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <Avatar src={specialist.profile_picture_url} name={specialist.name} size={48} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[15px] font-extrabold text-[#14110f] truncate">{specialist.name}</p>
              <AvailabilityPill status={specialist.availability} />
            </div>
            <p className="text-[12.5px] text-neutral-500">{specialist.title}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {(specialist.languages || []).slice(0, 3).map((l) => (
                <span key={l} className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded bg-[#faf6ec] text-[#7a5b00] font-semibold">
                  <Globe className="w-3 h-3" />{l}
                </span>
              ))}
            </div>
          </div>
          <button data-testid={`reassign-from-${specialist.id}`}
                  onClick={() => onOpenReassign(specialist)}
                  className="text-[11.5px] font-semibold text-[#c89200] hover:underline whitespace-nowrap">
            Bulk reassign…
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: 'Today',   value: specialist.workload.today,          max: cap.max_daily_appointments },
            { label: 'Week',    value: specialist.workload.this_week,      max: cap.max_weekly_appointments },
            { label: 'Clients', value: specialist.workload.active_clients, max: cap.max_active_clients },
          ].map((m) => (
            <div key={m.label}>
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">{m.label}</span>
                <span className="text-[13px] font-bold text-[#14110f]">
                  {m.value}{m.max ? <span className="text-neutral-400 font-normal"> / {m.max}</span> : ''}
                </span>
              </div>
              <div className="mt-1.5"><Bar value={m.value} max={m.max} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-neutral-100">
        <button data-testid={`expand-cap-${specialist.id}`}
                onClick={() => setExpanded((v) => !v)}
                className="w-full text-left px-5 py-2.5 text-[12.5px] font-semibold text-neutral-500 hover:bg-neutral-50 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5">
            <SettingsIcon className="w-3.5 h-3.5" />{expanded ? 'Hide capacity rules' : 'Edit capacity rules'}
          </span>
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        {expanded && (
          <div className="px-5 py-4 border-t border-neutral-100 bg-neutral-50/40">
            <CapacityEditor specialist={specialist} onSaved={onChanged} />
          </div>
        )}
      </div>
    </article>
  );
}

function ReassignModal({ open, onClose, fromSpecialist, specialists, onDone }) {
  const [toId, setToId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) { setToId(''); setReason(''); } }, [open]);
  if (!open) return null;
  const targets = specialists.filter((s) => s.id !== fromSpecialist?.id);
  const submit = async () => {
    if (!toId) { toast.error('Select a destination specialist'); return; }
    setSubmitting(true);
    try {
      const res = await api.adminBulkReassign({
        from_specialist_user_id: fromSpecialist?.id || null,
        to_specialist_user_id: toId,
        reason,
      });
      toast.success(`Reassigned ${res.moved} member${res.moved === 1 ? '' : 's'}`);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassign failed');
    } finally { setSubmitting(false); }
  };
  return (
    <div data-testid="reassign-modal"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
          <h3 className="font-bold text-[#14110f]">Bulk reassign</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#14110f]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[13px] text-neutral-600">
            Move every member currently assigned to <strong>{fromSpecialist?.name || 'no specialist'}</strong> to:
          </p>
          <select data-testid="reassign-target"
                  value={toId} onChange={(e) => setToId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none">
            <option value="">Select specialist…</option>
            {targets.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.title}</option>)}
          </select>
          <textarea data-testid="reassign-reason"
                    value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="Optional note for the audit log"
                    className="w-full px-3 py-2 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
        </div>
        <div className="px-5 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[13px] text-neutral-500 hover:text-[#14110f]">Cancel</button>
          <button data-testid="reassign-confirm" onClick={submit} disabled={submitting}
                  className="inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2 rounded-md text-[13px]">
            <Check className="w-3.5 h-3.5" />{submitting ? 'Reassigning…' : 'Reassign all'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAssignmentsPage() {
  const [tab, setTab] = useState('workloads');
  const [data, setData] = useState({ specialists: [], totals: {} });
  const [requests, setRequests] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(null);
  const [singleAssign, setSingleAssign] = useState({});

  const load = async () => {
    try {
      const [wk, req, un] = await Promise.all([
        api.adminSpecialistWorkloads(),
        api.adminListChangeRequests(),
        api.adminUnassignedMembers(),
      ]);
      setData(wk);
      setRequests(req);
      setUnassigned(un);
    } catch { toast.error('Could not load assignment data'); }
  };
  useEffect(() => { load(); }, []);

  const specialists = data.specialists || [];
  const specById = useMemo(() => Object.fromEntries(specialists.map((s) => [s.id, s])), [specialists]);

  const decide = async (req, decision) => {
    try {
      await api.adminDecideChangeRequest(req.id, decision);
      toast.success(`Request ${decision}d`);
      load();
    } catch { toast.error('Could not update request'); }
  };

  const assignOne = async (memberId) => {
    const target = singleAssign[memberId];
    if (!target) { toast.error('Pick a specialist'); return; }
    try {
      await api.adminAssignSingle({
        to_specialist_user_id: target,
        member_ids: [memberId],
        reason: 'admin manual assignment',
      });
      toast.success('Assigned');
      load();
    } catch { toast.error('Assign failed'); }
  };

  return (
    <AdminLayout
      title="Specialist assignments"
      subtitle="Workload, capacity rules, change requests and bulk reassignment in one place."
      actions={
        <button data-testid="assignments-refresh-btn" onClick={load}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-[12.5px] text-neutral-600 hover:bg-neutral-50">
          <RotateCcw className="w-3.5 h-3.5" />Refresh
        </button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { key: 'specialists',             label: 'Active specialists', icon: Users,        value: data.totals.specialists ?? 0 },
          { key: 'pending_change_requests', label: 'Change requests',    icon: ArrowRight,   value: data.totals.pending_change_requests ?? 0 },
          { key: 'unassigned_members',      label: 'Unassigned members', icon: AlertTriangle, value: data.totals.unassigned_members ?? 0 },
          { key: 'fully',                   label: 'Fully booked today',
            icon: PauseCircle,
            value: specialists.filter((s) => s.availability === 'fully_booked' || s.availability === 'away').length },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`stat-${c.key}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#fff8e3] text-[#c89200] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">{c.label}</p>
                <p className="text-[20px] font-extrabold text-[#14110f] leading-none mt-1">{c.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          const badge = t.key === 'requests' ? (data.totals.pending_change_requests || 0)
                       : t.key === 'unassigned' ? (data.totals.unassigned_members || 0)
                       : null;
          return (
            <button key={t.key} data-testid={`assignments-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition ${
                      isActive ? 'bg-[#14110f] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                    }`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
              {badge != null && badge > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white text-[#14110f]' : 'bg-[#f5b300] text-[#14110f]'}`}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'workloads' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {specialists.length === 0 && <p className="text-[13px] text-neutral-400 col-span-2">No specialists yet.</p>}
          {specialists.map((s) => (
            <WorkloadCard key={s.id} specialist={s}
                          onOpenReassign={(spec) => setReassignOpen(spec)}
                          onChanged={load} />
          ))}
        </div>
      )}

      {tab === 'requests' && (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          {requests.length === 0 ? (
            <p className="px-5 py-16 text-center text-[13px] text-neutral-400">No change requests yet.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {requests.map((r) => {
                const reqSpec = r.requested_specialist_user_id ? specById[r.requested_specialist_user_id] : null;
                return (
                  <li key={r.id} data-testid={`change-req-${r.id}`} className="px-5 py-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-[13px] ${
                        r.status === 'approved' ? 'bg-emerald-50 text-emerald-700'
                        : r.status === 'denied' ? 'bg-rose-50 text-rose-700'
                        : 'bg-amber-50 text-amber-700'
                      }`}>
                        {(r.member_name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[14px] font-bold text-[#14110f]">{r.member_name || r.member_email}</p>
                          <span className={`text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${
                            r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                            : r.status === 'denied' ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                            : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                          }`}>{r.status}</span>
                          {r.self_serve && (
                            <span className="text-[10.5px] font-semibold uppercase tracking-wider bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">self-serve</span>
                          )}
                        </div>
                        <p className="text-[12.5px] text-neutral-500 mt-0.5">{r.member_email} · {fmtDateTime(r.created_at)}</p>
                        <p className="text-[12.5px] text-neutral-700 mt-1.5">
                          Requesting: <strong>{reqSpec?.name || 'No preference'}</strong>
                        </p>
                        <p className="text-[13px] text-neutral-700 mt-1 whitespace-pre-wrap">{r.reason}</p>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex flex-col gap-1.5 shrink-0">
                          <button data-testid={`req-approve-${r.id}`}
                                  onClick={() => decide(r, 'approve')}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-semibold">
                            <Check className="w-3.5 h-3.5" />Approve
                          </button>
                          <button data-testid={`req-deny-${r.id}`}
                                  onClick={() => decide(r, 'deny')}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50 text-[12px] font-semibold">
                            <X className="w-3.5 h-3.5" />Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {tab === 'unassigned' && (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 flex items-center justify-between border-b border-neutral-100">
            <p className="text-[12.5px] text-neutral-500">
              {unassigned.length} member{unassigned.length === 1 ? '' : 's'} without a primary specialist.
            </p>
            <button data-testid="bulk-assign-all-btn"
                    onClick={() => setReassignOpen({ id: '', name: 'no specialist (unassigned)' })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#14110f] text-white text-[12px] font-semibold">
              <UserPlus className="w-3.5 h-3.5" />Bulk assign all
            </button>
          </div>
          {unassigned.length === 0 ? (
            <p className="px-5 py-16 text-center text-[13px] text-neutral-400">Everyone has a specialist. 🎉</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {unassigned.slice(0, 100).map((m) => (
                <li key={m.id} data-testid={`unassigned-row-${m.id}`}
                    className="px-5 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-neutral-100 text-neutral-500 flex items-center justify-center font-bold text-[13px]">
                    {(m.name || m.email || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-bold text-[#14110f] truncate">{m.name || m.email}</p>
                    <p className="text-[12px] text-neutral-500 truncate">{m.email} · joined {fmtDateTime(m.created_at)}</p>
                  </div>
                  <select data-testid={`unassigned-select-${m.id}`}
                          value={singleAssign[m.id] || ''}
                          onChange={(e) => setSingleAssign((s) => ({ ...s, [m.id]: e.target.value }))}
                          className="px-2 py-1.5 rounded-md border border-neutral-200 text-[12.5px] bg-white">
                    <option value="">Assign to…</option>
                    {specialists.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button data-testid={`unassigned-assign-${m.id}`}
                          onClick={() => assignOne(m.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] text-[12px] font-semibold">
                    <Check className="w-3.5 h-3.5" />Go
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ReassignModal open={!!reassignOpen} onClose={() => setReassignOpen(null)}
                     fromSpecialist={reassignOpen} specialists={specialists} onDone={load} />
    </AdminLayout>
  );
}
